import { ReactNode, useState } from "react";

import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import { Checkbox } from "atoms/checkbox/Checkbox";
import { TextField } from "atoms/text-field/TextField";

import OnboardingHeaderV3 from "../../components/OnboardingScreenHeader/OnboardingHeaderV3";
import { SmsComplianceLinks } from "../../components/SmsComplianceLinks/SmsComplianceLinks";
import { TransactionalSmsNotice } from "../../components/TransactionalSmsNotice/TransactionalSmsNotice";

type TranslateFn = ReturnType<typeof useTranslation>["t"];

// Marketing (promotional) SMS opt-in copy. Optional consent — names the sending practice
// only. When the practice name is unavailable we fall back to a generic "us" so a sender
// is still identified (required for SMS consent).
// The Terms/Privacy links are rendered alongside this label by SmsComplianceLinks: Twilio's
// compliance review (2026-07) requires them inside the opt-in disclosure block itself, so
// the footer links no longer satisfy the requirement.
const buildMarketingConsentLabel = (t: TranslateFn, practiceName?: string | null): string => {
  const sender = practiceName ? practiceName : t("loginOrSignUpForm.genericSender");
  return t("loginOrSignUpForm.marketingConsent", { sender });
};

export interface LoginOrSignUpFormProps {
  initialPhoneNumber?: string;
  onSubmit: (phoneNumber: string) => Promise<void>;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  hidePrimaryButton?: boolean;
  submitLabel?: string;
  /** New-user sign-up state: swaps the transactional notice for a required marketing opt-in. */
  consentRequired?: boolean;
  /** Overrides the default marketing opt-in copy (which otherwise names the practice). */
  consentLabel?: string;
  /** Practice name interpolated as the sender in the SMS copy (e.g. platform name). */
  practiceName?: string | null;
}

export const LoginOrSignUpForm = ({
  initialPhoneNumber = "",
  onSubmit,
  className = "",
  prefix,
  suffix,
  hidePrimaryButton = false,
  submitLabel,
  consentRequired = false,
  consentLabel,
  practiceName,
}: LoginOrSignUpFormProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(formatPhoneNumber(initialPhoneNumber));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>(undefined);
  const [isMarketingOptIn, setIsMarketingOptIn] = useState(false);

  const isValidPhoneNumber = value.length >= 14;
  // During new-user sign-up the marketing opt-in must be checked before we can submit
  // (parity with production). Enforced in handleSubmit so no path — button, Enter key, or
  // programmatic — can bypass it.
  const isConsentSatisfied = !consentRequired || isMarketingOptIn;
  const canSubmit = isValidPhoneNumber && isConsentSatisfied;

  const handlePhoneNumberInput = (phone: string) => {
    const formattedPhoneNumber = formatPhoneNumber(phone);
    setValue(formattedPhoneNumber);
    // Clear error when user starts typing
    if (phoneNumberError) {
      setPhoneNumberError(undefined);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) {
      return;
    }
    try {
      setIsSubmitting(true);
      await onSubmit?.(value);
    } catch (err) {
      if (err instanceof Error) {
        setPhoneNumberError(err.message);
      } else {
        setPhoneNumberError(t("loginOrSignUpForm.submitError"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyUp = (keyName: string) => {
    if (keyName === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className={`flex w-full max-w-sm flex-col ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? (
        <div className="mt-12 flex w-full items-center justify-center">{prefix}</div>
      ) : null}

      {/* Header */}
      <OnboardingHeaderV3 title={t("loginOrSignUpForm.title")} align="center" className="mb-4" />

      <TextField
        type="tel"
        label={t("loginOrSignUpForm.phoneNumberLabel")}
        name="tel"
        autoComplete="tel"
        autoFocus
        onChange={handlePhoneNumberInput}
        value={value}
        pattern="[0-9]*"
        placeholder={t("loginOrSignUpForm.phoneNumberPlaceholder")}
        invalid={!!phoneNumberError}
        supportingText={phoneNumberError}
        onKeyUp={handleKeyUp}
        disabled={isSubmitting}
        className="!rounded-lg !border-0 !bg-white shadow-none !ring-0"
        labelClassName="!hidden"
      />

      {/* Below the phone input: the transactional SMS disclosure, visible from page open.
          During new-user sign-up the marketing opt-in (required before submit) takes its
          place. */}
      {consentRequired ? (
        <div className="mt-4 w-full">
          <Checkbox
            label={consentLabel || buildMarketingConsentLabel(t, practiceName)}
            checked={isMarketingOptIn}
            onChange={setIsMarketingOptIn}
            textStyle="text-body3"
            inputClassName="!m-0"
            labelClassName="!text-[11px]"
          />
          {/* Sibling of the Checkbox, never inside its <label>: the atom wraps input+label in
              a native <label>, so a link placed within it would toggle consent on tap.
              Indented to line up with the label text column — this call site zeroes the
              input's margin (inputClassName="!m-0"), leaving w-6 (24px) + the label's
              ml-1.5 (6px) = 30px. */}
          <SmsComplianceLinks className="ml-[30px] mt-1.5" />
        </div>
      ) : (
        <TransactionalSmsNotice practiceName={practiceName} className="mt-4" />
      )}

      {/* Primary button — gated on a valid phone number and, for sign-up, the opt-in. */}
      {!hidePrimaryButton ? (
        <div className="mt-4">
          <Button fullWidth disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
            <span className="font-sans text-body1 font-normal">
              {isSubmitting
                ? t("common.loading")
                : (submitLabel ?? t("loginOrSignUpForm.defaultSubmitLabel"))}
            </span>
          </Button>
        </div>
      ) : null}

      {/* Optional suffix slot */}
      {suffix ? <div className="mt-6 w-full">{suffix}</div> : null}
    </div>
  );
};

const formatPhoneNumber = (inputValue: string) => {
  if (!inputValue) {
    return inputValue;
  }

  const phoneNumber = removeNonDigits(inputValue);

  if (phoneNumber.length < 4) {
    return phoneNumber;
  }

  if (phoneNumber.length < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const removeNonDigits = (inputValue: string) => inputValue.replace(/[^\d]/g, "");
